'use strict';

import React from "react";
import PropTypes from "prop-types";
import {sortBy} from "underscore";

import Card from "react-bootstrap/Card";

import {FaExternalLinkAlt} from "react-icons/fa";

import HelpPopover from "@uw-it-sis/lib-react/lib/HelpPopover";
import {capitalize, makeUniqueId} from "@uw-it-sis/lib-react/lib/Utils";

import {a11yCode} from "@uw-it-sis/lib-react-myplan/lib/CourseUtils";
import {getGeneralPlaceholderName} from "@uw-it-sis/lib-react-myplan/lib/GeneralPlaceHolderConstants";
import GenEdUtils from "@uw-it-sis/lib-react-myplan/lib/GenEdUtils";

import PlanItem from "./PlanItem";
import {PlanItemType, PlanViewType} from "../lib/Constants";

const PlanItemListHelp = {
    "planned": "Courses, sections, and any planned items you have added to this quarter.",
    "backup": "Courses, sections, and any planned items you marked as Backup for this quarter. Backup sections will not be sent to Registration.",
    "recommended": "Courses and other recommendations from your adviser. Accept the recommendation to add it to your plan.",
    "registered": "Course sections that you are enrolled in."
}

/**
 * Component for the display of a list of plan items
 */
const PlanItemList = ({type, viewType = PlanViewType.plan, items = [], termColor, termPeriod, openForPlanning = true, isPublished = false, showBorder = false, creditComponent}) => {
    if ( items.length === 0 ) {
        return null;
    }

    let planItemList = type === PlanItemType.registered ? sortBy(items, (obj) => obj.courseDetails.code) : items;

    let planItems = planItemList.map( (item, index) => {
        let a11yLabel;
        if (item.hasOwnProperty("placeHolder") && item.placeHolder) {
            a11yLabel = getGeneralPlaceholderName(item.placeHolderCode, item.placeHolderValue);
            if (item.planItemDataObject.refObjType === "uw.course.genedrequirement") {
                a11yLabel = `General Education Requirement: ${GenEdUtils.getTitleFromCode(a11yLabel)}`;
            }
        } else {
            if (item.courseDetails.hasOwnProperty("curriculumTitle") && item.courseDetails.hasOwnProperty("courseNumber")) {
                a11yLabel = `${item.courseDetails.curriculumTitle} ${a11yCode(item.courseDetails.courseNumber)}`;
            } else {
                a11yLabel = a11yCode(item.courseDetails.code);
            }
        }

        return (
            <li key={index} className="my-2" aria-label={a11yLabel}>
                <PlanItem item={item} type={type} openForPlanning={openForPlanning} isPublished={isPublished} viewType={viewType} />
            </li>
        );
    });

    let title = type === PlanItemType.registered && termPeriod === "past" ? "completed" : type;
    let dropLink = null;
    if ( type === PlanItemType.registered && openForPlanning && viewType === PlanViewType.plan ) {
        dropLink =
            <a href="https://sdb.admin.uw.edu/students/uwnetid/register.asp" target="_blank" className="text-nowrap">
                Drop courses
                <FaExternalLinkAlt className="ms-2" aria-hidden={true} />
            </a>;
    }

    let id = `plan-item-list-${title}-${makeUniqueId()}`;

    let help = null;
    if ( PlanItemListHelp.hasOwnProperty(title) ) {
        help = <HelpPopover title={capitalize(title)} placement="top" className="ms-2 text-muted">{PlanItemListHelp[title]}</HelpPopover>
    }

    let cardClasses = `${showBorder ? "border-start-0 border-end-0 border-bottom-0 border-3 rounded-0 " : ""} pt-3 px-3 pb-2`;

    return (
        <Card className={cardClasses} border={showBorder ? termColor : undefined}>
            <div className="d-flex flex-wrap justify-content-between">
                <h3 id={id} className="h5 text-uppercase mb-0 me-2 d-inline-flex align-items-center">{title} {help}</h3>
                {dropLink}
            </div>
            <ul aria-labelledby={id} className="list-unstyled mb-0 clearfix">
                {planItems}
            </ul>
            {creditComponent}
        </Card>
    );
}

PlanItemList.propTypes = {
    type: PropTypes.oneOf(Object.keys(PlanItemType)).isRequired,
    items: PropTypes.array.isRequired,
    termColor: PropTypes.string.isRequired,
    termPeriod: PropTypes.string.isRequired,
    openForPlanning: PropTypes.bool.isRequired,
    isPublished: PropTypes.bool.isRequired,
    showBorder: PropTypes.bool,
    viewType: PropTypes.oneOf(Object.keys(PlanViewType)),
    creditComponent: PropTypes.object
};

export default PlanItemList;